"use client";
import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import Image from "next/image";
import apiClient from "@/api/config";
import { hasTrackedShare, markShareTracked } from "@/api/utils/session";

// ─── Activity type constants (mirrors the ongoing classes page) ───────────────
const ActivityType = {
  SHARE_CLICK: 1,
} as const;

function decodeSlug(
  slug: string,
): { courseName: string; studentId: string; courseId: string } | null {
  try {
    const decoded = decodeURIComponent(escape(atob(decodeURIComponent(slug))));
    const parts = decoded.split("|");
    if (parts.length < 2) return null;
    // Support old format (2 parts) and new format (3 parts)
    const courseId = parts.length >= 3 ? parts[parts.length - 1] : "";
    const studentId = parts[parts.length - (parts.length >= 3 ? 2 : 1)];
    const courseName = parts
      .slice(0, parts.length - (parts.length >= 3 ? 2 : 1))
      .join("|");
    if (!courseName || !studentId) return null;
    return { courseName, studentId, courseId };
  } catch {
    return null;
  }
}
// ─── Component ────────────────────────────────────────────────────────────────
export default function ShareRedirectPage() {
  const params = useParams();
  const code = params?.code as string | undefined;
  const [status, setStatus] = useState<"loading" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!code) {
      setErrorMsg("Invalid share link.");
      setStatus("error");
      return;
    }

    const parsed = decodeSlug(code);
    if (!parsed) {
      setErrorMsg("Malformed share link.");
      setStatus("error");
      return;
    }

    const { courseName, studentId, courseId } = parsed;

    // Redirect target: the public course listing page
    // Adjust this URL to wherever your course detail page lives
    const destination = courseId
      ? `https://student.laplagemetaverse.org/course/${courseId}`
      : `https://student.laplagemetaverse.org`;

    // One browser counts once per share link (tracked in a small cookie)
    const alreadyTracked = hasTrackedShare(code);

    const track = async () => {
      if (!alreadyTracked) {
        try {
          // activitySharedCode = course title  |  activityType = 1 (share click)
          // Matches the pattern: /public/studentTrackingActivity/{courseName}/{activityType}/{studentId}
          await apiClient.put(
            `/public/studentTrackingActivity/${encodeURIComponent(courseName)}/${ActivityType.SHARE_CLICK}/${studentId}`,
          );
          markShareTracked(code);
        } catch (err) {
          // Tracking failure must not block redirect
          console.error("Tracking failed:", err);
        }
      }
      // Always redirect after attempting to track
      window.location.href = destination;
    };

    track();
  }, [code]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F4F6F9] px-4">
        <div className="bg-white rounded-2xl shadow-md p-10 flex flex-col items-center gap-4 max-w-sm w-full text-center">
          <p className="text-red-600 font-semibold text-lg">{errorMsg}</p>
          <a href="/" className="text-primary underline text-sm font-medium">
            Return to homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F4F6F9] px-4">
      <div className="bg-white rounded-2xl shadow-md p-10 flex flex-col items-center gap-6 max-w-sm w-full text-center">
        {/* Logo */}
        <Image
          src="/logo.svg"
          alt="La Plage Meta Verse"
          width={120}
          height={40}
          priority
        />

        {/* Spinner */}
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />

        <div className="flex flex-col gap-1">
          <p className="font-semibold text-gray-800 text-base">Loading…</p>
          <p className="text-sm text-gray-500">Please wait...</p>
        </div>
      </div>
    </div>
  );
}
